import {
  AddBusinessOutlined,
  AttachFileOutlined,
  AutoAwesomeOutlined,
  CommentOutlined,
  HubOutlined,
  KeyboardArrowRightRounded,
  StoreOutlined,
} from "@mui/icons-material";
import { Card } from "@mui/material";
import Button from "@mui/material/Button";
import { isArray, isNil, size } from "lodash";
import { memo, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDrilldownBase } from "../../../../pipelines/PipelineContext";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import TitledHeaderWithSearch from "../../../../components/layout/TitledHeaderWithSearch";
import Txt from "../../../../components/typography/Txt";
import SubsectionHeader from "../../../../components/ui/SubsectionHeader";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import LoanDocumentGroupFilesCard from "../../shared/LoanDocumentGroupFilesCard";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanPropertiesTable from "./LoanPropertiesTable";
import PropertyDrilldown from "./PropertyDrilldown";
import { DetailSection } from "../../../../_src_shared/common/DetailSection/DetailSection";
import AssociationsSelector from "../../../../components/associations/AssociationsSelector";
import EnhancedCommentItemRenderer from "../../../../_src_shared/comments/EnhancedCommentItemRenderer";
import { useCommentUtilsHook } from "../../../../hooks/utils/useCommentUtilsHook";
import useAssociationUtils from "../../../../components/associations/hooks/useAssociationUtils";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

const LoanDrilldownPropertiesTab = ({ value, tab }) => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const drilldownBase = useDrilldownBase();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { id: loanId } = useParams();
  const properties = useMemo(
    () => loanDrilldown?.subjectProperties || [],
    [loanDrilldown?.subjectProperties]
  );
  const ref = useRef();

  const [quickFilter] = useState(null);

  // Find the selected property based on URL parameter
  const selectedProperty = useMemo(() => {
    if (!propertyId || !properties.length) return null;
    return properties.find((property) => property._id === propertyId) || null;
  }, [propertyId, properties]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <TitledHeaderWithSearch
        title={"Properties"}
        borderColor={"#eeeeee"}
        endContent={
          <Flx ac gap={1}>
            <ViewRedesignButton />
            <AddPropertyButton />
          </Flx>
        }
      />

      <ScreenContent sx={{ p: 3 }}>
        {/* --- TABLE --- */}
        <SubsectionHeader
          title={"Loan Properties"}
          icon={<StoreOutlined className="thin" fontSize="small" />}
        />
        <Card
          sx={{
            p: 0,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <LoanPropertiesTable
            rowData={properties}
            ref={ref}
            selectedId={selectedProperty?._id}
            quickFilterText={quickFilter}
            showAllColumns={false}
          />
        </Card>

        {/* --- PREVIEW AND FILES --- */}
        <Flx wrap fw gap={4} sx={{ mt: 1, flexGrow: 1, flexShrink: 1, px: 0 }}>
          {isNil(selectedProperty) ? null : (
            <Flx
              className={"left-col"}
              column
              sx={{
                flexShrink: 1,
                flexGrow: 1,
                flexBasis: "800px",
              }}
            >
              <SubsectionHeader
                title={"All Properties"}
                onClick={() => navigate(`${drilldownBase}/${loanId}/properties`)}
                icon={<StoreOutlined className="thin" fontSize="small" />}
              >
                <KeyboardArrowRightRounded className="thin" />
                <Txt bold>{addressLabel(selectedProperty?.address)}</Txt>
              </SubsectionHeader>
              <PropertyDrilldown selectedId={selectedProperty?._id} />
            </Flx>
          )}

          <Flx
            gap={3}
            column
            sx={{ flexGrow: 1, flexShrink: 1, flexBasis: "400px", py: 3 }}
          >
            {isNil(selectedProperty?._id) ? null : (
              <>
                <PropertyAssociationsSection property={selectedProperty} />
                <PropertyCommentsSection property={selectedProperty} />
              </>
            )}

            <AllPropertyRelatedFilesContent />
          </Flx>
        </Flx>
      </ScreenContent>
    </LoanDrilldownTabPanel>
  );
};

const PropertyAssociationsSection = ({ property }) => {
  const { getTotalArrayItems } = useAssociationUtils();
  return (
    <>
      <DetailSection
        title={`Property Associations (${getTotalArrayItems(property?.associations)})`}
        variant="card"
        collapsible={true}
        defaultExpanded={true}
        icon={<HubOutlined className="thin" fontSize="small" />}
      >
        <AssociationsSelector
          associationTypeName="subjectProperties"
          associationTypeId={property?._id}
          associations={property.associations}
        />
      </DetailSection>

      {/* <DetailSection
                title="Associations"
                variant="card"
                collapsible={true}
                defaultExpanded={true}
                icon={<HubOutlined className="thin" fontSize="small" />}
              >
                <AssociationsSelector
                  associationTypeName="subjectProperties"
                  associationTypeId={selectedProperty?._id}
                  associations={selectedProperty.associations}
                />
              </DetailSection> */}
    </>
  );
};

const PropertyCommentsSection = ({ property }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getCommentsAssociatedWithProperty } = useCommentUtilsHook();

  const associatedComments = useMemo(() => {
    const targetId = property?._id;
    return getCommentsAssociatedWithProperty(loanDrilldown?.comments, targetId);
  }, [getCommentsAssociatedWithProperty, loanDrilldown?.comments, property]);
  return (
    <>
      <DetailSection
        title={`${addressLabel(property?.address)} Related Comments (${size(associatedComments)})`}
        // title="Associations"
        variant="card"
        collapsible={true}
        defaultExpanded={true}
        contentSx={{ p: 0, maxHeight: "720px", overflowY: "auto" }}
        icon={<CommentOutlined className="thin" fontSize="small" />}
      >
        <PropertyCommentsRenderer comments={associatedComments} />
      </DetailSection>
    </>
  );
};

const PropertyCommentsRenderer = ({ comments, borrowerId }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getCommentsAssociatedWithBorrower } = useCommentUtilsHook();

  const associatedComments = useMemo(() => {
    if (isArray(comments)) {
      return comments;
    }
    const targetId = borrowerId;
    return getCommentsAssociatedWithBorrower(loanDrilldown?.comments, targetId);
  }, [
    comments,
    getCommentsAssociatedWithBorrower,
    loanDrilldown?.comments,
    borrowerId,
  ]);
  return (
    <>
      <EnhancedCommentItemRenderer
        loan={loanDrilldown}
        comments={associatedComments}
      />
    </>
  );
};

const AllPropertyRelatedFilesContent = ({ selectedId }) => {
  return (
    <DetailSection
      title={`Related Files (All Properties)`}
      // title="Associations"
      // variant="card"
      collapsible={true}
      defaultExpanded={true}
      icon={<AttachFileOutlined className="thin" fontSize="small" />}
    >
      <FlxGrid wrap gap={2} width="420px">
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Appraisal"}
        />
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Construction Documents"}
        />
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Insurance Docs"}
        />
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Leases & Rents"}
        />
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Property Documents"}
        />
        <LoanDocumentGroupFilesCard
          showIfEmpty
          hideChart
          showGroupIcon
          docGroup={"Title Docs"}
        />
      </FlxGrid>
    </DetailSection>
  );
};

const AddPropertyButton = memo(() => {
  const dispatch = useDispatch();

  const handleToggle = () => {
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_PROPERTY));
  };

  return (
    <Button startIcon={<AddBusinessOutlined />} onClick={handleToggle}>
      Add Property
    </Button>
  );
});

const ViewRedesignButton = memo(() => {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      startIcon={<AutoAwesomeOutlined sx={{ fontSize: 16 }} />}
      onClick={() => navigate("../properties")}
      sx={{ textTransform: "none", fontWeight: 600 }}
    >
      View Redesign
    </Button>
  );
});

export default LoanDrilldownPropertiesTab;
